import React, { useMemo } from 'react';
import { 
  Megaphone, 
  Users, 
  UserPlus,
  Calendar as CalendarIcon, 
  Target, 
  Plus, 
  MessageSquare, 
  FileText, 
  CheckSquare,
  Vote,
  Shield,
  ShieldAlert,
  Network,
  LayoutList,
  Mail,
  BookOpen
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  Badge, 
  Button, 
  ErrorBanner 
} from '../components/ui';
import { PulseStack } from '../components/pulse/PulseStack';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useModule } from '../hooks/useModules';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useCreateNode } from '../hooks/useNode';
import { OnboardingWizard } from '../components/onboarding/OnboardingWizard';
import { EmptyState } from '../components/ui/EmptyState';

// New Widget Components
import { StatCards } from '../components/dashboard/StatCards';
import { RecentActivity } from '../components/dashboard/RecentActivity';
import { GuardianActivity } from '../components/dashboard/GuardianActivity';
import { ActionMenu } from '../components/dashboard/ActionMenu';
import { WelcomeAboardGuide } from '../components/dashboard/WelcomeAboardGuide';
import { ToolDraftResumeCard } from '../components/dashboard/ToolDraftResumeCard';
import { TOOL_EVENTS, track } from '../lib/analytics';

import { useDiscussions } from '../hooks/useDiscussions';
import { useUserDashboard } from '../hooks/useUserDashboard';
import { useGoals } from '../hooks/useGoals';

export const Dashboard = () => {
  const { node, isUniversal, getTerm, isLoading: isTenantLoading, isGlobal, clearActiveOrg, switchOrg } = useTenant();
  const { memberships, isLoading: isUserLoading, error: authError, isSyncFailed, isBackendSynced, isSignedIn, retrySync } = useCurrentUser();
  const { can, isAdmin, role, isSuperAdmin, isSystemAdmin } = usePermissions();
  const navigate = useNavigate();
  const createNode = useCreateNode();
  const queryClient = useQueryClient();
  const [onboardingDismissed, setOnboardingDismissed] = React.useState(false);

  const { data: dashboardData, isLoading: isDashboardLoading } = useUserDashboard(isUniversal);
  const { data: discussionsData, isLoading: isDiscussionsLoading } = useDiscussions();
  const { data: goals, isLoading: isGoalsLoading } = useGoals(node?.id || '');

  // Handle pending hub creation from landing page wizard
  // GUARD: STRICT: User must be signed in AND confirmed synced with backend MongoDB database.
  // Never auto-create if sync failed, sync is incomplete, or user is not yet confirmed in the backend.
  React.useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    if (!isSignedIn || !isBackendSynced || isSyncFailed) return;
    
    const pendingHub = localStorage.getItem('pending_hub_creation');
    if (pendingHub && memberships.length === 0 && !isUserLoading) {
      try {
        const data = JSON.parse(pendingHub);
        // Remove it immediately to prevent infinite loops if the mutation fails or triggers re-renders
        localStorage.removeItem('pending_hub_creation');
        createNode.mutate(data, {
          onSuccess: (newNode: any) => {
            // Route through switchOrg so TenantContext state updates, not just localStorage.
            // Keep pending_tool_org_draft so the dashboard can link back to the tool result.
            if (newNode?.slug) {
              switchOrg(newNode.slug, '/dashboard');
            }
            track(TOOL_EVENTS.orgCreated, {
              source: 'pending_hub',
              source_tool: data.sourceTool,
              org_slug: newNode?.slug || data.slug,
            });
            toast.success(`Organization "${data.name}" provisioned successfully! You are the registered Admin.`);
          },
          onError: (err: any) => {
            console.error("Failed to auto-create pending hub:", err);
            localStorage.setItem('pending_hub_creation', JSON.stringify(data));
            toast.error(err?.response?.data?.message || "Please complete setup to customize your organization name.");
            navigate('/create-org');
          }
        });
      } catch (e) {
        console.error("Error parsing pending hub creation data", e);
        localStorage.removeItem('pending_hub_creation');
      }
    }
  }, [memberships.length, isUserLoading, isSyncFailed, isBackendSynced, isSignedIn, createNode, switchOrg, navigate]);

  const isLoadingDashboard = isTenantLoading || isUserLoading || (isUniversal && isDashboardLoading);
  
  const hasPulse = useModule('PULSE');
  const hasCalendar = useModule('CALENDAR');
  const hasGoals = useModule('GOALS');
  const hasNewsletter = useModule('NEWSLETTER');
  const hasDiscussions = useModule('DISCUSSIONS');
  const hasElections = useModule('ELECTIONS');
  const hasRequirements = useModule('REQUIREMENTS');

  // Dashboard configuration
  const displayTitle = isUniversal ? "Universal Command Center" : node ? `${node.name} Dashboard` : "Dashboard";
  const orgCount = memberships.length;
  
  const showAdminWidgets = isAdmin || isSuperAdmin || isSystemAdmin || can('view_analytics');

  const dependentActivity = dashboardData?.dependentActivity || [];

  const [overrideVisibility, setOverrideVisibility] = React.useState<Record<string, boolean>>({});

  const isDismissed = typeof localStorage !== 'undefined' && node?.id
    ? localStorage.getItem(`welcome_guide_dismissed_${node.id}`) === 'true'
    : false;

  const showWelcomeGuide = Boolean(
    node?.id && (overrideVisibility[node.id] !== undefined ? overrideVisibility[node.id] : !isDismissed)
  );
  
  // Safely extract discussions content from paginated response
  const discussions = Array.isArray(discussionsData) 
    ? discussionsData.slice(0, 3) 
    : (discussionsData as any)?.content?.slice(0, 3) || [];

  const actions = useMemo(() => [
    { id: 'invite', label: `Invite ${getTerm('member')}`, icon: UserPlus, path: '/members?action=invite', isEnabled: true, capability: 'manage_members' as const },
    { id: 'announcement', label: `New ${getTerm('pulse')}`, icon: Megaphone, path: '/pulse', isEnabled: hasPulse, capability: 'create_announcement' as const },
    { id: 'event', label: 'New Event', icon: CalendarIcon, path: '/calendar', isEnabled: hasCalendar, capability: 'create_event' as const },
    { id: 'goal', label: 'New Goal', icon: Target, path: '/goals', isEnabled: hasGoals, capability: 'create_goal' as const },
    { id: 'discussion', label: 'New Discussion', icon: MessageSquare, path: '/discussions', isEnabled: hasDiscussions, capability: 'manage_message_board' as const },
    { id: 'requirement', label: 'New Requirement', icon: CheckSquare, path: '/requirements', isEnabled: hasRequirements, capability: 'create_requirement' as const },
    { id: 'newsletter', label: 'New Newsletter', icon: FileText, path: '/newsletter', isEnabled: hasNewsletter, capability: 'create_newsletter' as const },
    { id: 'referral', label: 'Refer Organization', icon: Users, path: '/referrals', isEnabled: true, capability: 'manage_referrals' as const },
    { id: 'election', label: 'New Election', icon: Vote, path: '/elections', isEnabled: hasElections, capability: 'manage_elections' as const },
  ].filter(a => a.isEnabled && can(a.capability)), [getTerm, hasPulse, hasCalendar, hasGoals, hasDiscussions, hasRequirements, hasNewsletter, hasElections, can]);

  if (isLoadingDashboard) {
    return (
      <div className="space-y-6">
        <div className="h-40 bg-gray-200 rounded-none w-full animate-pulse dark:bg-slate-800" />
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gray-200 rounded-none w-48 animate-pulse dark:bg-slate-800" />
          <div className="h-8 bg-gray-200 rounded-none w-32 animate-pulse dark:bg-slate-800" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-none animate-pulse dark:bg-slate-800" />)}
        </div>
        <div className="h-32 bg-gray-200 rounded-none w-full animate-pulse dark:bg-slate-800" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-64 bg-gray-200 rounded-none animate-pulse dark:bg-slate-800" />
          <div className="h-64 bg-gray-200 rounded-none animate-pulse dark:bg-slate-800" />
        </div>
      </div>
    );
  }



  if (!node && !isUniversal) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center bg-[#FFD600] border-8 border-black shadow-[12px_12px_0px_#000000] dark:bg-slate-900 dark:border-white dark:shadow-[12px_12px_0px_#FFFFFF]">
        <div className="w-16 h-16 bg-black text-white flex items-center justify-center border-4 border-black mb-6">
          <Shield size={32} />
        </div>
        <h2 className="text-4xl md:text-5xl font-black font-display text-black dark:text-gray-100 mb-2 uppercase tracking-tighter">Organization Not Found</h2>
        <p className="text-black dark:text-gray-300 font-mono text-sm max-w-sm mb-8 uppercase tracking-widest border-l-4 border-black pl-4">
          The organization you are looking for does not exist or has been removed.
        </p>
        <div className="flex gap-4">
          <Button onClick={() => window.location.href = '/'} variant="secondary">Go to Home Page</Button>
        </div>
      </div>
    );
  }

  const isPreviewingNode = !isGlobal && Boolean(node) && (isSuperAdmin || isSystemAdmin);

  if (memberships.length === 0 && !isUniversal && !isPreviewingNode) {
    // CRITICAL: If sync failed, the user record may not exist in the backend yet.
    // Block all hub creation flows until sync succeeds — otherwise nodes get created
    // with no corresponding user record, permanently breaking the account.
    if (isSyncFailed) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center bg-[#FFD600] border-8 border-black shadow-[12px_12px_0px_#000000] dark:bg-slate-900 dark:border-white dark:shadow-[12px_12px_0px_#FFFFFF]">
          <div className="w-16 h-16 bg-black text-white flex items-center justify-center border-4 border-black mb-6">
            <Shield size={32} />
          </div>
          <h2 className="text-4xl md:text-5xl font-black font-display text-black dark:text-gray-100 mb-2 uppercase tracking-tighter">Account Setup Incomplete</h2>
          <p className="text-black dark:text-gray-300 font-mono text-sm max-w-sm mb-8 uppercase tracking-widest border-l-4 border-black pl-4">
            Your account could not be fully registered with our servers. Please retry to complete setup before creating or joining an organization.
          </p>
          <Button
            onClick={() => retrySync(false)}
            variant="primary"
            className="bg-black text-white font-bold uppercase tracking-wider px-8 py-4"
          >
            Retry Account Setup
          </Button>
        </div>
      );
    }

    const onboardingCompleted = typeof localStorage !== 'undefined' && localStorage.getItem('onboarding_completed');
    
    if (!onboardingCompleted && !onboardingDismissed) {
      return (
        <OnboardingWizard 
          onComplete={() => {
            setOnboardingDismissed(true);
            queryClient.invalidateQueries({ queryKey: ['user-memberships-live'] });
            queryClient.invalidateQueries({ queryKey: ['user-memberships-mock'] });
            queryClient.invalidateQueries({ queryKey: ['node-config-full'] });
          }} 
        />
      );
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center bg-white border-8 border-black shadow-[12px_12px_0px_#000000] dark:bg-slate-900 dark:border-white dark:shadow-[12px_12px_0px_#FFFFFF]">
        <div className="w-16 h-16 bg-[#0044FF] text-white flex items-center justify-center border-4 border-black mb-6">
          <Network size={32} />
        </div>
        <h2 className="text-4xl md:text-5xl font-black font-display text-black dark:text-gray-100 mb-2 uppercase tracking-tighter">No active organizations</h2>
        <p className="text-black dark:text-gray-300 font-mono text-sm max-w-sm mb-6 uppercase tracking-widest border-l-4 border-black pl-4">
          You aren't currently a member of any organizations on this platform. Check pending invitations or create a brand new organization.
        </p>

        {!isGlobal && node && (
          <div className="mb-8 p-4 bg-[#FFD600] border-4 border-black text-left max-w-md w-full shadow-[4px_4px_0px_#000000]">
            <p className="text-xs font-black uppercase tracking-wider font-mono text-black">
              Currently previewing: <span className="underline">{node.name}</span>
            </p>
            <p className="text-[11px] font-mono text-black/80 mt-1">
              You are inspecting this organization node with platform administrator privileges.
            </p>
            <div className="mt-3 flex gap-2">
              <Button onClick={() => clearActiveOrg()} variant="secondary" className="text-xs py-1.5 px-3">
                Exit Org Preview
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-4">
          {(isSuperAdmin || isSystemAdmin) && (
            <Button onClick={() => navigate('/system-admin')} variant="primary" className="gap-2 bg-black text-white hover:bg-[#0044FF]">
              <ShieldAlert size={16} /> System Admin Console
            </Button>
          )}
          <Button onClick={() => navigate('/invitations')} variant="secondary" className="gap-2">
            <Mail size={16} /> Check Invitations
          </Button>
          <Button onClick={() => navigate('/create-org')} variant="primary" className="gap-2 bg-[#0044FF]">
            <Plus size={16} /> Create Org
          </Button>
        </div>
      </div>
    );
  }

  const isEnrolledInCurrentNode = memberships.some(m => m.nodeSlug === node?.slug);

  return (
    <div className="flex flex-col gap-12 pt-8 pb-24 max-w-[1400px] mx-auto">
      {authError && (
        <ErrorBanner 
          title="Sync Error" 
          message={authError.message || 'We encountered an error while loading your profile and memberships.'} 
          onRetry={() => window.location.reload()} 
        />
      )}

      {!isGlobal && !isUniversal && !isEnrolledInCurrentNode && (isSuperAdmin || isSystemAdmin) && (
        <div className="bg-[#FFD600] border-4 border-black p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-[4px_4px_0px_#000000]">
          <div>
            <p className="text-xs font-black uppercase tracking-widest font-mono text-black">
              Administrator Preview Mode — {node?.name}
            </p>
            <p className="text-[11px] font-mono text-black/80 mt-0.5">
              You are viewing this organization node as a platform administrator.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => clearActiveOrg()} variant="secondary" size="sm" className="text-xs">
              Exit Preview (Return to Platform)
            </Button>
            <Button onClick={() => navigate('/system-admin')} variant="primary" size="sm" className="text-xs bg-black text-white hover:bg-[#0044FF]">
              System Admin
            </Button>
          </div>
        </div>
      )}

      {can('edit_settings') && !isUniversal && showWelcomeGuide && (
        <WelcomeAboardGuide 
          onDismiss={() => {
            if (node?.id) {
              localStorage.setItem(`welcome_guide_dismissed_${node.id}`, 'true');
              setOverrideVisibility(prev => ({ ...prev, [node.id]: false }));
            }
          }}
        />
      )}

      <ToolDraftResumeCard />

      {can('edit_settings') && !isUniversal && (
        <section className="bg-[#FFD600] border-8 border-black dark:border-white p-8 md:p-12 relative overflow-hidden text-black shadow-[12px_12px_0px_#000000] dark:shadow-[12px_12px_0px_#FFFFFF]">
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
              <div className="flex-1 max-w-3xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-black text-white flex items-center justify-center border-4 border-black">
                    <Shield size={32} />
                  </div>
                  <h2 className="text-5xl md:text-7xl font-black font-display uppercase tracking-tighter leading-none break-words">
                    Admin<br/>Workspace
                  </h2>
                </div>
                <p className="text-black font-mono text-sm md:text-base uppercase tracking-widest leading-relaxed max-w-xl border-l-4 border-black pl-4">
                  AUTHORIZED AS: <span className="font-bold">{role}</span><br/>
                  FULL SYSTEM ACCESS GRANTED. OVERRIDE MODULES, MANAGE DIRECTORY, AND VIEW RAW ANALYTICS.
                </p>
                <div className="flex flex-wrap gap-2 mt-8">
                  <Badge className="bg-black text-white rounded-none border-none px-3 py-1 font-mono uppercase tracking-widest text-[10px]">Manage {getTerm('member')}s</Badge>
                  <Badge className="bg-black text-white rounded-none border-none px-3 py-1 font-mono uppercase tracking-widest text-[10px]">Module Config</Badge>
                  <Badge className="bg-black text-white rounded-none border-none px-3 py-1 font-mono uppercase tracking-widest text-[10px]">Ledger Admin</Badge>
                  <Badge className="bg-black text-white rounded-none border-none px-3 py-1 font-mono uppercase tracking-widest text-[10px]">Vault Security</Badge>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto mt-4 md:mt-0">
                <Button 
                  variant="secondary" 
                  className="bg-white text-black hover:bg-white border-4 border-black rounded-none shadow-[4px_4px_0px_#000000] uppercase font-bold tracking-widest py-6 px-8 text-xs transition-transform hover:-translate-y-1 hover:shadow-[6px_6px_0px_#000000] flex items-center gap-2" 
                  onClick={() => {
                    const next = !showWelcomeGuide;
                    if (node?.id) {
                      if (!next) {
                        localStorage.setItem(`welcome_guide_dismissed_${node.id}`, 'true');
                      } else {
                        localStorage.removeItem(`welcome_guide_dismissed_${node.id}`);
                      }
                      setOverrideVisibility(prev => ({ ...prev, [node.id]: next }));
                    }
                  }}
                >
                  <BookOpen size={16} /> {showWelcomeGuide ? 'Hide Guide' : 'Welcome Guide'}
                </Button>
                <Button variant="primary" className="bg-black text-white hover:bg-[#0044FF] border-4 border-black rounded-none shadow-[4px_4px_0px_#000000] uppercase font-bold tracking-widest py-6 px-8 text-xs transition-transform hover:-translate-y-1 hover:shadow-[6px_6px_0px_#000000]" onClick={() => navigate('/settings')}>Org Settings</Button>
                <Button variant="secondary" className="bg-white text-black hover:bg-white border-4 border-black rounded-none shadow-[4px_4px_0px_#000000] uppercase font-bold tracking-widest py-6 px-8 text-xs transition-transform hover:-translate-y-1 hover:shadow-[6px_6px_0px_#000000]" onClick={() => navigate('/members')}>Directory Admin</Button>
              </div>
            </div>
          </div>
          {/* Bauhaus Geometric Accents */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#0044FF] border-l-8 border-b-8 border-black -translate-y-1/2 translate-x-1/2 rounded-none" />
        </section>
      )}

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b-8 border-black pb-6">
        <div>
          <h1 className="text-4xl md:text-6xl font-black font-display text-black dark:text-white uppercase tracking-tighter break-words leading-none">
            {displayTitle}
          </h1>
          {isUniversal && <p className="text-sm font-mono uppercase tracking-widest text-gray-500 mt-4 border-l-4 border-black dark:border-white pl-3">Showing overview across {orgCount} organizations</p>}
        </div>
        {!isUniversal && (
          <div className="flex flex-wrap items-center gap-4">
             <Button size="sm" variant="secondary" className="hidden md:flex border-2 border-black rounded-none uppercase font-bold tracking-widest text-[10px] py-4" onClick={() => window.open(`/${node?.slug}/pulse`, '_blank')}>View Public Org</Button>
             {can('create_sub_orgs') && node?.tier !== 'LOCAL' && (
               <Button size="sm" variant="primary" className="gap-2 bg-[#0044FF] text-white border-2 border-black rounded-none uppercase font-bold tracking-widest text-[10px] py-4 hover:bg-black" onClick={() => navigate('/sub-orgs')}>
                 <Plus size={14} /> New {getTerm('childNode')}
               </Button>
             )}
             <ActionMenu actions={actions} />
          </div>
        )}
      </div>

      {showAdminWidgets && (
        <StatCards isUniversal={isUniversal} orgCount={orgCount} hasPulse={hasPulse} />
      )}

      {isUniversal && dependentActivity.length > 0 && (
        <GuardianActivity dependentActivity={dependentActivity} />
      )}

      <PulseStack />

      {/* Main Content Area */}
      <div className={cn("grid grid-cols-1 gap-8", showAdminWidgets ? "lg:grid-cols-3" : "md:grid-cols-2")}>
        {showAdminWidgets && <RecentActivity isUniversal={isUniversal} />}

        <div className={cn("space-y-8", !showAdminWidgets && "md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8 !space-y-0")}>
          <div className="bg-white border-8 border-black dark:border-white p-6 shadow-[8px_8px_0px_#000000] dark:bg-black dark:shadow-[8px_8px_0px_#FFFFFF] flex flex-col">
            <div className="flex items-center justify-between border-b-4 border-black pb-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-black text-white flex items-center justify-center">
                  <Target size={20} />
                </div>
                <h3 className="font-black font-display text-2xl uppercase tracking-tight text-black dark:text-white">{isUniversal ? "My Orgs" : `Active ${getTerm('goals')}`}</h3>
              </div>
            </div>
            <div className="space-y-4 flex-1">
              {isUniversal ? (
                memberships.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-6 text-center border-2 border-dashed border-black dark:border-white">
                    <p className="text-xs font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-4">
                      No organizations joined yet.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <Button onClick={() => navigate('/create-org')} size="sm" variant="primary" className="text-[10px] bg-[#0044FF]">
                        <Plus size={12} className="mr-1" /> Create Org
                      </Button>
                      <Button onClick={() => navigate('/invitations')} size="sm" variant="secondary" className="text-[10px]">
                        <Mail size={12} className="mr-1" /> Invitations
                      </Button>
                    </div>
                  </div>
                ) : (
                  memberships.map(m => (
                    <div key={m.id} onClick={() => switchOrg(m.nodeSlug, '/dashboard')} 
                      className="flex items-center justify-between p-3 border-2 border-black hover:bg-black hover:text-white transition-colors cursor-pointer group dark:border-white dark:hover:bg-white dark:hover:text-black">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#FFD600] text-black border-2 border-black flex items-center justify-center text-xs font-bold font-mono">
                          {(m.nodeName || '').substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-base font-bold font-display uppercase tracking-tight group-hover:text-white dark:group-hover:text-black">{m.nodeName}</p>
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-mono group-hover:text-gray-300 dark:text-gray-400">{m.role}</p>
                        </div>
                      </div>
                      {m.unreadCount > 0 && <Badge className="bg-[#0044FF] text-white rounded-none font-mono text-xs">{m.unreadCount}</Badge>}
                    </div>
                  ))
                )
              ) : (
                <>
                  {isGoalsLoading ? (
                    <div className="text-xs text-gray-500 font-mono text-center py-8 uppercase tracking-widest border-2 border-dashed border-black dark:border-white">Loading goals...</div>
                  ) : goals && goals.length > 0 ? (
                    goals.slice(0, 3).map(goal => {
                      const percent = goal.targetValue > 0 ? Math.min(100, Math.round((goal.currentValue / goal.targetValue) * 100)) : 0;
                      return (
                        <div key={goal.id} className="border-2 border-black p-3 group">
                          <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest font-mono mb-3">
                            <span className="text-black dark:text-white truncate mr-2">{goal.title}</span>
                            <span className="text-[#0044FF]">{percent}%</span>
                          </div>
                          <div className="h-4 border-2 border-black bg-white overflow-hidden w-full relative">
                            <div className="absolute top-0 left-0 h-full bg-[#0044FF] transition-all duration-500" style={{ width: `${percent}%` }} />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <EmptyState
                      icon={Target}
                      title="No Active Goals"
                      description="Goals help track progress on organizational milestones. Create your first goal to start measuring what matters."
                      actions={[{ label: 'Create a Goal', onClick: () => navigate('/goals') }]}
                      tips={['Goals track percentage-based progress toward any target', 'All members can see active goals on their dashboard']}
                      className="border-4 border-dashed"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {hasDiscussions && (
            <div className="bg-[#F5F5F0] border-8 border-black dark:border-white p-6 shadow-[8px_8px_0px_#000000] dark:bg-black dark:shadow-[8px_8px_0px_#FFFFFF] flex flex-col">
              <div className="flex items-center justify-between border-b-4 border-black pb-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0044FF] text-white flex items-center justify-center border-2 border-black">
                    <LayoutList size={20} />
                  </div>
                  <h3 className="font-black font-display text-2xl uppercase tracking-tight text-black dark:text-white">{getTerm('discussions')}</h3>
                </div>
                <Button variant="ghost" size="sm" className="border-2 border-black rounded-none uppercase font-mono tracking-widest text-[10px] font-bold hover:bg-black hover:text-white" onClick={() => navigate('/discussions')}>View Board</Button>
              </div>
              <div className="space-y-4">
                {isDiscussionsLoading ? (
                  <div className="text-xs text-gray-500 font-mono text-center py-8 uppercase tracking-widest border-2 border-dashed border-black dark:border-white">Loading discussions...</div>
                ) : discussions.length > 0 ? (
                  discussions.map((topic: any) => (
                    <div 
                      key={topic.id} 
                      onClick={() => navigate(`/discussions?topic=${topic.id}`)}
                      className="p-4 border-2 border-black bg-white hover:bg-[#FFD600] hover:translate-x-1 transition-all cursor-pointer group dark:bg-slate-950 dark:hover:bg-[#FFD600] dark:hover:text-black"
                    >
                      <p className="text-sm font-bold font-display uppercase tracking-tight text-black line-clamp-1">{topic.title}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-[10px] text-gray-600 font-mono uppercase tracking-widest">BY {topic.authorName}</p>
                        <span className="text-[10px] text-black font-bold">•</span>
                        <p className="text-[10px] text-[#0044FF] font-bold font-mono uppercase tracking-widest">
                          {typeof topic.createdAt === 'string' && topic.createdAt.includes('T') ? 'RECENTLY' : (topic.createdAt || 'JUST NOW').toUpperCase()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <EmptyState
                    icon={LayoutList}
                    title="No Active Discussions"
                    description="Start a conversation to get your community talking. Discussions support threaded replies and parent-org cascading."
                    actions={[{ label: 'Start Discussion', onClick: () => navigate('/discussions') }]}
                    tips={['Discussions cascade down to child organizations when enabled', 'Supports markdown, images, and embedded video links']}
                    className="border-4 border-dashed"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
